/*
 * Copyright (c) 2021.  Blacksheepz media B.V.
 * https://www.blacksheepz-media.com
 * Ramon van Ophuizen
 */
// Importing necessary libraries and components
import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import { useQuery, useMutation } from "@apollo/client";
import { useThemeData } from '../context/ThemeDataContext';
import { useForm } from "react-hook-form";
import { GET_PHOTOS_FROM_ALBUM, UPDATE_IMAGE } from "../queries";
import { useParams } from "react-router-dom";

// Material UI components
import { Grid, Card, CardHeader, CardActions, CardMedia, Stack, Divider, TextField, Button, Skeleton, Typography, Snackbar, Alert } from '@mui/material';

function ClassCards(props) {
    const { site_id } = useThemeData().themeData.site;
    const PHOTO_URL = process.env.REACT_APP_PHOTO_URL;
    const folder_id = useParams().folder_id;
    const school_id = useParams().school_id;
    const { register, handleSubmit } = useForm();
    // State for Snackbar UI
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const ariaLabel = { 'aria-label': 'description' };

    // Query to fetch photos from the album
    const { loading: loadingPhotos, data: dataPhotos } = useQuery(GET_PHOTOS_FROM_ALBUM, {
        variables: { site_id, folder_id, hoofdfoto: 1 },
        skip: folder_id === undefined,
        notifyOnNetworkStatusChange: true,
    });

    // Mutation to update school photo
    const [updateSchoolPhoto] = useMutation(UPDATE_IMAGE, {
        ignoreResults: true,
        onCompleted: () => {

            setSnackbarMessage("Successfully updated photo");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        },
        onError: (error) => {
            console.log(error);
            setSnackbarMessage("Problem updating photo");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    });

    const goTo = (data) => {
        props.history.push({
            pathname: `/classes/${school_id}`,
            state: { data }
        });
    };

    const onSubmit = (data, e) => {
        data.photo.forEach((child) => {
            let photo = dataPhotos.albumPhotos.find(item => item.id === child.id);

            // Check sync value and update accordingly
            let updateData = {
                id: child.id,
                site_id: photo.site_id,
                folder_id: photo.folder_id,
                extra_email: photo.extra_email,
                login_name: photo.login_name,
                file_name: photo.file_name,
                email: photo.email,
                afgeschermd: photo.afgeschermd,
                verstuur_inlogkaartje_mail: photo.verstuur_inlogkaartje_mail,
            };

            if (dataPhotos.albumPhotos[0]?.folder?.folderparent[0]?.folderparent[0]?.sync === 1) {
                updateData = {
                    ...updateData,
                    voornaam: child.voornaam,
                    achternaam: child.achternaam,
                    tussenvoegsel: child.tussenvoegsel,
                    kind_naam: '',
                    student_id: child.studentId,
                };
            } else {
                updateData = {
                    ...updateData,
                    kind_naam: child.name,
                    student_id: child.studentId,
                };
            }

            updateSchoolPhoto({ variables: { input: updateData } });

        });
     //   e.target.reset();
    };

    // console.log(dataPhotos.albumPhotos[0]?.folder?.folderparent[0]?.folderparent[0]);

    return (
        <>
            {loadingPhotos
                ?
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Skeleton sx={{ margin: "auto" }} variant="text" width={450} height={70} />
                            <Skeleton sx={{ margin: "auto" }} variant="text" width={300} height={50} />
                        </Grid>
                        {[...Array(12)].map((x, i) => (
                            <Grid item xs={6} sm={4}>
                                <Card>
                                <Skeleton variant="text" sx={{ margin: "auto" }} width={"80%"} />
                                    <Skeleton sx={{ margin: "auto" }} variant="rectangular" width={140} height={160} />
                                    <Skeleton sx={{ margin: "auto" }} variant="text"  width={"90%"} height={40} />
                                </Card>
                        </Grid>
                        ))}
                    </Grid>
                :
                <>
                    <Snackbar
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        open={snackbarOpen}
                        autoHideDuration={6000}
                        onClose={() => setSnackbarOpen(false)}
                    >
                        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                    <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h1" component="h1">
                                {dataPhotos.albumPhotos[0]?.folder?.folderparent[0]?.folderparent[0]?.folder_naam || "School Name Not Available"}
                            </Typography>
                            <Typography variant="h2" component="h2">
                                {dataPhotos.albumPhotos[0]?.folder?.folderparent[0]?.folder_naam || "Group Name Not Available"}
                            </Typography>
                        </Grid>
                        {dataPhotos && dataPhotos.albumPhotos.map((photo, i) => (
                        <Grid item xs={6} sm={4}>
                            <Card>
                                <CardHeader
                                    sx={{ whiteSpace: "normal" }} // Allow wrapping
                                    title={
                                        <>
                                        {dataPhotos.albumPhotos[0]?.folder?.folderparent[0]?.folderparent[0]?.sync !== 1 ? (
                                            <> {photo.kind_naam ? photo.kind_naam : photo.file_name}</>
                                        ) : (
                                            <>
                                                {photo.voornaam} {photo.tussenvoegsel} {photo.achternaam}
                                            </>
                                        )}
                                            {photo.student_id && ( // Only render if student_id is not null
                                                <>
                                                    <br />
                                                    {`ID: ${photo.student_id}`}
                                                </>
                                            )}
                                        </>
                                    }
                                />
                                <CardMedia
                                    component="img"
                                    image={`${PHOTO_URL}${site_id}/${photo.folder.folder_id}/0/0/0/0/${photo.id}/${photo.file_name}`}
                                    alt="filename"
                                    height="140px"
                                    title="img url"
                                    sx={{
                                        objectFit: 'scale-down',
                                    }}
                                />
                                <CardActions sx={{ justifyContent: "center" }}>
                                    <Stack direction="column" spacing={2}> {/* Add Stack with vertical direction */}
                                        {dataPhotos.albumPhotos[0]?.folder?.folderparent[0]?.folderparent[0]?.sync !== 1 ? (
                                            <TextField
                                                variant="standard"
                                                {...register(`photo.${i}.name`)}
                                                placeholder="Name"
                                                inputProps={ariaLabel}
                                                defaultValue={photo.kind_naam}
                                            />
                                        ) : (
                                            <>
                                                <TextField
                                                    variant="standard"
                                                    {...register(`photo.${i}.voornaam`)}
                                                    placeholder="Voornaam"
                                                    inputProps={ariaLabel}
                                                    defaultValue={photo.voornaam}
                                                />

                                                <TextField
                                                    variant="standard"
                                                    {...register(`photo.${i}.tussenvoegsel`)}
                                                    placeholder="Tussenvoegsel"
                                                    inputProps={ariaLabel}
                                                    defaultValue={photo.tussenvoegsel}
                                                />

                                                <TextField
                                                    variant="standard"
                                                    {...register(`photo.${i}.achternaam`)}
                                                    placeholder="Achternaam"
                                                    inputProps={ariaLabel}
                                                    defaultValue={photo.achternaam}
                                                />
                                            </>
                                        )}
                                        <TextField
                                            variant="standard"
                                            {...register(`photo.${i}.studentId`)}
                                            placeholder="Student ID"
                                            defaultValue={photo.student_id}
                                            inputProps={ariaLabel}
                                        />
                                        <input type="hidden" value={photo.id} {...register(`photo.${i}.id`)} />
                                    </Stack>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <Stack
                            spacing={2}
                            my={2}
                            direction="row"
                            justifyContent="center"
                            alignItems="center"
                            divider={<Divider orientation="vertical" flexItem />}
                        >
                            <Button variant="text" color="primary" onClick={() => { goTo(folder_id) }}>
                                Back
                            </Button>
                            <Button variant="contained" color="primary" type="submit">
                                Save
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
                </form>
            </>
            }
        </>

    );
}

export default React.memo(withTranslation(['classcards'])(ClassCards));
